// Shared write path for PlayerInput commands.

import { useCallback } from 'react';
import type { ContentType, GameElementType } from '@airhead-games/shared/types';
import {
  generateCommandId,
  getGameElementCollectionKey,
  isValidElementId,
  parseElementId,
} from '@airhead-games/shared/utilities';
import { useRoomConfig } from './useRoomConfig';
import { useRoomGameElements } from './useRoomGameElements';
import { useRoomMyPlayer } from './useRoomMyPlayer';
import { useRoomSocket } from './useRoomSocket';

export function usePlayerInput() {
  const { roomCode } = useRoomConfig();
  const { getElementById } = useRoomGameElements();
  const myPlayer = useRoomMyPlayer();
  const { sendCommand } = useRoomSocket();

  return useCallback(
    (
      targetElementId: string,
      content: ContentType,
      parentElementIdOverride?: string
    ) => {
      const parsedTargetId = parseElementId(targetElementId);

      // Silently ignore incomplete room/player state,
      // malformed IDs, or non-game element IDs instead of surfacing UI errors.
      if (!parsedTargetId || !myPlayer || !roomCode) return;

      if (!getGameElementCollectionKey(parsedTargetId.elementType)) return;
      const elementType = parsedTargetId.elementType as GameElementType;

      const targetElement = getElementById(targetElementId);

      // Some inputs reparent their target element, such as votes moving to a
      // selected answer or answers moving to a selected prompt.
      const parentElementId =
        parentElementIdOverride && isValidElementId(parentElementIdOverride)
          ? parentElementIdOverride
          : targetElement?.parentId;

      // Match room pick-list behavior: votes can override parent via third argument.
      // so later UI migrations can swap rendering without changing room events.
      sendCommand({
        id: generateCommandId(),
        type: 'PlayerInput',
        elementType,
        playerId: myPlayer.playerId,
        content,
        elementId: targetElementId,
        parentElementId,
        roomCode,
        senderRole: myPlayer.role,
        clientTimestamp: Date.now(),
      });
    },
    [getElementById, myPlayer, roomCode, sendCommand]
  );
}
